import React, { useState } from 'react'
import { AlertTriangle, Plus, RefreshCw, Users } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useIcps } from '../hooks/useIcps'
import { ICP_NAME_MAX_LENGTH, MAX_ICPS, type Icp, type IcpInput } from '../types'
import IcpCard from './IcpCard'
import IcpForm from './IcpForm'

type Props = {
  workspaceId: string
  role: 'owner' | 'admin' | 'member'
}

type FormState =
  | { mode: 'create' }
  | { mode: 'edit'; icp: Icp }
  | { mode: 'duplicate'; icp: Icp }

function toInput(icp: Icp): IcpInput {
  return {
    name: icp.name,
    industries: icp.industries,
    companySizes: icp.companySizes,
    jobTitles: icp.jobTitles,
    geography: icp.geography,
    buyingSignals: icp.buyingSignals,
  }
}

// "Fintech CTOs" → "Fintech CTOs (copy)", trimmed so it still fits the name limit.
function copyName(name: string): string {
  const suffix = ' (copy)'
  return `${name.slice(0, ICP_NAME_MAX_LENGTH - suffix.length)}${suffix}`
}

const IcpsSection: React.FC<Props> = ({ workspaceId, role }) => {
  const { currentTheme: t } = useTheme()
  const { icps, loading, error, reload, create, update, remove, makePrimary } = useIcps(workspaceId)
  const [form, setForm] = useState<FormState | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Icp | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // Owners and admins manage ICPs (the backend enforces the same split); members read.
  const canManage = role === 'owner' || role === 'admin'
  const atLimit = icps.length >= MAX_ICPS

  const run = async (action: () => Promise<unknown>) => {
    setActionError(null)
    setBusy(true)
    try {
      await action()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const handleSubmit = async (input: IcpInput) => {
    if (!form) return
    if (form.mode === 'edit') await update(form.icp.id, input)
    else await create(input)
    setForm(null)
  }

  const handleConfirmDelete = () => {
    const target = deleteTarget
    if (!target) return
    void run(async () => {
      await remove(target.id)
      setDeleteTarget(null)
    })
  }

  const formTitle = form?.mode === 'edit' ? 'Edit ICP' : form?.mode === 'duplicate' ? 'Duplicate ICP' : 'New ICP'
  const formInitial = !form || form.mode === 'create'
    ? null
    : form.mode === 'edit'
      ? toInput(form.icp)
      : { ...toInput(form.icp), name: copyName(form.icp.name) }

  const newButton = canManage && (
    <button
      type="button"
      onClick={() => { setActionError(null); setForm({ mode: 'create' }) }}
      disabled={atLimit}
      className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
      style={{ background: t.accentGradient ?? t.accent, color: t.buttonText }}
    >
      <Plus size={15} />
      New ICP
    </button>
  )

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <p className="text-sm max-w-xl" style={{ color: t.secondrytext }}>
          An ideal customer profile (ICP) describes one segment you sell to. Zeph uses the <strong>primary</strong> ICP for
          prospecting and qualification, and asks which ICP to target when it matters.
        </p>
        <div className="flex items-center gap-3">
          {atLimit && canManage && (
            <span className="text-xs" style={{ color: t.mutedText }}>Limit of {MAX_ICPS} ICPs reached</span>
          )}
          {newButton}
        </div>
      </div>

      {actionError && !form && !deleteTarget && (
        <div className="rounded-xl px-4 py-3 text-sm" role="alert" style={{ backgroundColor: t.errorBackground, color: t.errorText }}>
          {actionError}
        </div>
      )}

      {loading && (
        <div className="flex flex-col gap-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-40 rounded-2xl animate-pulse" style={{ backgroundColor: t.hoverBackground }} />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl" style={{ backgroundColor: t.errorBackground }}>
            <AlertTriangle size={24} style={{ color: t.errorText }} />
          </div>
          <div>
            <p className="font-semibold text-base" style={{ color: t.text }}>Couldn&apos;t load ICPs</p>
            <p className="text-sm mt-1" style={{ color: t.mutedText }}>{error}</p>
          </div>
          <button
            type="button"
            onClick={reload}
            className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium"
            style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      )}

      {!loading && !error && icps.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl" style={{ backgroundColor: t.hoverBackground }}>
            <Users size={24} style={{ color: t.mutedText }} />
          </div>
          <div>
            <p className="font-semibold text-base" style={{ color: t.text }}>No ICPs yet</p>
            <p className="text-sm mt-1 max-w-md" style={{ color: t.mutedText }}>
              {canManage
                ? 'Add an ICP so Zeph knows who to find, qualify and write to.'
                : 'An owner or admin hasn’t added an ICP yet — Zeph can’t target the right customers until they do.'}
            </p>
          </div>
          {canManage && <div className="mt-1">{newButton}</div>}
        </div>
      )}

      {!loading && !error && icps.length > 0 && (
        <div className="flex flex-col gap-3">
          {icps.map((icp) => (
            <IcpCard
              key={icp.id}
              icp={icp}
              canManage={canManage}
              busy={busy}
              onEdit={(target) => { setActionError(null); setForm({ mode: 'edit', icp: target }) }}
              onDuplicate={(target) => { setActionError(null); setForm({ mode: 'duplicate', icp: target }) }}
              onMakePrimary={(target) => void run(() => makePrimary(target.id))}
              onDelete={(target) => { setActionError(null); setDeleteTarget(target) }}
            />
          ))}
        </div>
      )}

      <Sheet open={form !== null} onOpenChange={(open) => { if (!open) setForm(null) }}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{formTitle}</SheetTitle>
            <SheetDescription>
              Saving counts as reviewing this ICP.
            </SheetDescription>
          </SheetHeader>
          {form && (
            <IcpForm
              key={form.mode === 'create' ? 'create' : `${form.mode}-${form.icp.id}`}
              initial={formInitial}
              submitLabel={form.mode === 'edit' ? 'Save changes' : 'Create ICP'}
              onSubmit={handleSubmit}
              onCancel={() => setForm(null)}
            />
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <DialogContent
          className="p-7 sm:p-8 rounded-2xl shadow-xl max-w-md w-[92vw] border-none"
          style={{ background: t.secondary, color: t.text }}
        >
          <DialogHeader className="items-center gap-2">
            <DialogTitle className="text-lg font-semibold text-center" style={{ color: t.text }}>
              Delete “{deleteTarget?.name}”?
            </DialogTitle>
            <p className="text-sm leading-relaxed text-center" style={{ color: t.mutedText }}>
              Zeph will stop using this ICP. This can’t be undone.
            </p>
          </DialogHeader>
          {actionError && (
            <p className="text-sm text-center" role="alert" style={{ color: t.errorText }}>{actionError}</p>
          )}
          <DialogFooter className="flex flex-col-reverse gap-2.5 mt-5 sm:flex-row">
            <button
              type="button"
              onClick={() => setDeleteTarget(null)}
              disabled={busy}
              className="flex-1 h-10 rounded-lg font-medium text-sm"
              style={{ background: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={busy}
              className="flex-1 h-10 rounded-lg font-medium text-sm bg-red-500 hover:bg-red-600 text-white disabled:opacity-60"
            >
              {busy ? 'Deleting…' : 'Delete'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default React.memo(IcpsSection)
